import type { ResolvedSalesOrder, ResolvedSalesOrderItem, ResolvedSalesShippingOption, ResolvedSalesSpecialFee } from "../types/sales";
import { getSalesResolvedDiscountDisplay } from "./getSalesResolvedDiscountDisplay";
import { orderLineKey } from "utils/orderResolveLinePricing";
import { round2 } from "utils/fixedAmountDiscountAllocation";
import { resolveTaxableFromSources, findCatalogRowById } from "../utils/readResolveTaxable";
import { mergeResolvedShippingAmounts } from "../utils/shippingAmountSync";

export type SalesResolveUiSync = {
  productTaxMap: Record<string, number>;
  productTaxPctMap: Record<string, number>;
  taxBreakdown: { product: number; shipping: number; specialFee: number };
  productTotals: { subTotal: number; discount: number; productTax: number };
  effectiveMap: Record<string, number>;
  basePriceMap: Record<string, number>;
  feePayloadPatch: {
    specialfee: unknown[];
    total_amount: number;
    total_tax_amount: number;
  } | null;
  shipPayloadPatch: { shipping_option: unknown[] } | null;
  discountDisplay: { code: string; amount: number };
  subtotalAmount: number;
  totalAmount: number;
  overallTax: number;
};

export type ApplySalesResolveResponseOptions = {
  requestedDiscountCode?: string;
  userClearedCoupon?: boolean;
  /** UI fee rows before resolve — preserve catalog taxable flags over resolve echo. */
  existingFeeRows?: unknown[] | null;
  existingShipRows?: unknown[] | null;
  specialFeeCatalog?: unknown[] | null;
  shippingCostCatalog?: unknown[] | null;
  /** Order Totals card manual shipping edit — do not overwrite amount from resolve/qty recalc. */
  userEditedShippingAmount?: boolean;
};

function resolvedItemMap(items: ResolvedSalesOrderItem[]): Map<string, ResolvedSalesOrderItem> {
  return new Map(
    items.map((li) => [
      orderLineKey(String(li.product_id ?? ""), String(li.variant_id ?? "")),
      li,
    ])
  );
}

/**
 * Map resolved sales order → UI tax/discount/totals state (replaces TaxEngine + CouponDiscount).
 */
export function applySalesResolveResponse(
  resolved: ResolvedSalesOrder,
  options?: ApplySalesResolveResponseOptions
): SalesResolveUiSync {
  const items = Array.isArray(resolved.items) ? resolved.items : [];
  const byKey = resolvedItemMap(items);

  const productTaxMap: Record<string, number> = {};
  const productTaxPctMap: Record<string, number> = {};
  let productTax = 0;
  let subTotal = 0;

  for (const li of items) {
    const key = orderLineKey(String(li.product_id ?? ""), String(li.variant_id ?? ""));
    const lineTax = Number(li.item_tax_info?.total_product_tax_amount ?? 0);
    const lineSub = Number(
      li.item_sub_total ??
        Number(li.changed_unit_price ?? li.unit_retail_price ?? 0) * Number(li.quantity ?? 0)
    );
    productTaxMap[key] = round2(lineTax);
    productTaxPctMap[key] = Number(li.item_tax_info?.tax_percentage ?? 0);
    productTax += lineTax;
    subTotal += lineSub;
  }

  const charges = resolved.order_charges?.[0] as
    | {
        shipping_option?: ResolvedSalesShippingOption[];
        specialfee?: ResolvedSalesSpecialFee[];
      }
    | undefined;
  const shipRows = charges?.shipping_option ?? [];
  const feeRows = charges?.specialfee ?? [];
  const shippingTax = shipRows.reduce((s, r) => s + Number(r.shipping_tax_amount ?? 0), 0);
  const feeTax = feeRows.reduce((s, f) => s + Number(f.specialfee_tax_amount ?? 0), 0);

  const effectiveMap: Record<string, number> = {};
  const basePriceMap: Record<string, number> = {};
  byKey.forEach((li, key) => {
    if (String(li.inventory_source ?? "") === "custom") return;
    const changed = Number(li.changed_unit_price ?? 0);
    const retail = Number(li.unit_retail_price ?? changed);
    if (changed > 0) effectiveMap[key] = round2(changed);
    if (retail > 0) basePriceMap[key] = round2(retail);
  });

  const feeSubtotal = feeRows.reduce((s, f) => s + Number(f.specialfee_amount ?? 0), 0);
  const overallTax = Number(
    resolved.order_tax_summary?.over_all_tax_amount ?? productTax + shippingTax + feeTax
  );

  const requested = options?.requestedDiscountCode;
  const discountDisplay =
    options?.userClearedCoupon || requested === ""
      ? { code: "", amount: 0 }
      : getSalesResolvedDiscountDisplay(resolved, requested);

  return {
    productTaxMap,
    productTaxPctMap,
    taxBreakdown: {
      product: round2(productTax),
      shipping: round2(shippingTax),
      specialFee: round2(feeTax),
    },
    productTotals: {
      subTotal: round2(resolved.subtotal_amount ?? subTotal),
      discount: discountDisplay.amount,
      productTax: round2(productTax),
    },
    effectiveMap,
    basePriceMap,
    feePayloadPatch: {
      specialfee: feeRows.map((f) => {
        const id = String(f.specialfee_id ?? "");
        const catalogRow = findCatalogRowById(options?.specialFeeCatalog, id, [
          "id",
          "_id",
          "specialfee_id",
        ]);
        const existingRow = findCatalogRowById(options?.existingFeeRows, id, [
          "specialfee_id",
          "id",
          "_id",
        ]);
        return {
          specialfee_id: id,
          specialfee_name: String(f.specialfee_name ?? ""),
          specialfee_amount: Number(f.specialfee_amount ?? 0),
          specialfee_tax_percentage: Number(f.specialfee_tax_percentage ?? 0),
          specialfee_tax_amount: Number(f.specialfee_tax_amount ?? 0),
          specialfee_taxable: resolveTaxableFromSources(
            [existingRow, catalogRow, f as Record<string, unknown>],
            ["specialfee_taxable", "taxable", "is_taxable"],
            true
          ),
        };
      }),
      total_amount: round2(feeSubtotal),
      total_tax_amount: round2(feeTax),
    },
    shipPayloadPatch: {
      shipping_option: mergeResolvedShippingAmounts(
        shipRows.map((r) => {
          const shipId = String(r.shipping_id ?? "");
          const catalogRow = findCatalogRowById(options?.shippingCostCatalog, shipId, [
            "_id",
            "id",
            "shipping_id",
          ]);
          const existingRow = findCatalogRowById(options?.existingShipRows, shipId, [
            "shipping_id",
            "shipping__id",
            "id",
            "_id",
          ]);
          return {
            shipping__id: shipId,
            shipping_id: shipId,
            shipping_name: String(r.shipping_name ?? ""),
            shipping_type: String(r.shipping_type ?? ""),
            shipping_amount: Number(r.shipping_amount ?? 0),
            shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
            shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
            shipping_taxable: resolveTaxableFromSources(
              [existingRow, catalogRow, r as Record<string, unknown>],
              ["shipping_taxable", "taxable", "is_taxable"],
              true
            ),
            shipping_delivarydate: String(r.shipping_delivery_date ?? ""),
            shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
            is_customer_pickup: r.is_customer_pickup === true,
          };
        }),
        (options?.existingShipRows as Record<string, unknown>[] | null | undefined) ?? [],
        !!options?.userEditedShippingAmount
      ),
    },
    discountDisplay,
    subtotalAmount: round2(resolved.subtotal_amount ?? subTotal),
    totalAmount: round2(resolved.total_amount ?? 0),
    overallTax: round2(overallTax),
  };
}

export type SyncSelectedProductsFromResolvedInput = {
  productId: string;
  variantId: string;
  quantity?: number;
  unitPrice?: number;
  unit_retail_price?: number;
  changed_unit_price?: number;
  discount_on_unit_quantity?: number;
  discount_on_total_quantity?: number;
  item_sub_total?: number;
  total_price?: number;
  total?: number;
};

/** Merge resolve line pricing into selectedProducts rows for table display. */
export function mergeSelectedProductsFromResolved<T extends SyncSelectedProductsFromResolvedInput>(
  rows: T[],
  resolved: ResolvedSalesOrder | null | undefined,
  manualUnitPriceMap: Record<string, number>
): T[] {
  if (!resolved?.items?.length) return rows;
  const byKey = resolvedItemMap(resolved.items);

  return rows.map((row) => {
    const key = orderLineKey(row.productId, row.variantId);
    if (manualUnitPriceMap[key] !== undefined) return row;
    const li = byKey.get(key);
    if (!li) return row;

    const changed = Number(li.changed_unit_price ?? row.unitPrice ?? 0);
    const retail = Number(li.unit_retail_price ?? changed);
    const qty = Math.max(1, Number(row.quantity ?? li.quantity ?? 1));
    const lineSub = Number(li.item_sub_total ?? changed * qty);

    return {
      ...row,
      unitPrice: changed,
      unit_retail_price: retail,
      changed_unit_price: changed,
      discount_on_unit_quantity: Number(li.discount_on_unit_quantity ?? 0),
      discount_on_total_quantity: Number(li.discount_on_total_quantity ?? 0),
      item_sub_total: lineSub,
      total_price: Number(li.total_price ?? lineSub),
      total: Number(li.total_price ?? lineSub),
    };
  });
}
